import {Chip as MuiChip, ChipProps} from "@mui/material";
import React, {ReactNode, useMemo} from "react";
import {Colors, Text} from "../../ui";

const createChipProps = (colors: typeof Colors) => ({
  height: {
    small: "22px",
    medium: "26px",
    large: "32px",
  },
  padding: {
    small: "2px 6px",
    medium: "2px 8px",
    large: "4px 8px",
  },
  font: {
    small: "12px",
    medium: "14px",
    large: "14px",
  },
  iconSize: {
    small: "16px",
    medium: "16px",
    large: "20px",
  },
  borderRadius: {
    small: "6px",
    medium: "8px",
    large: "8px",
  },
  color: {
    primary: {text: colors.bw.white, background: colors.primary._800, borderColor: colors.primary._800},
    secondary: {text: colors.primary._800, background: colors.primary._100, borderColor: colors.primary._400},
    inherit: {text: colors.grey._1000, background: colors.grey._100, borderColor: colors.grey._400},
    error: {text: colors.red._900, background: colors.red._100, borderColor: colors.red._400},
    warning: {text: colors.orange._900, background: colors.orange._100, borderColor: colors.orange._400},
    success: {text: colors.green._900, background: colors.green._100, borderColor: colors.green._400},
    info: {text: colors.secondary._900, background: colors.secondary._100, borderColor: colors.secondary._400},
    yellow: {text: colors.yellow._800, background: colors.yellow._100, borderColor: colors.yellow._600},
    blue: {text: colors.blue._800, background: colors.blue._100, borderColor: colors.blue._400},
    pink: {text: colors.pink._800, background: colors.pink._100, borderColor: colors.pink._400},
    neon: {text: colors.primary._800, background: colors.neon._300, borderColor: colors.neon._400},
    neutral: {text: colors.grey._900, background: Colors.bw.white, borderColor: colors.grey._100},
  },
});

export type ChipType = ChipProps & {
  size_?: "small" | "medium" | "large";
  label: string;
  variant_?:
    | "secondary"
    | "inherit"
    | "primary"
    | "success"
    | "error"
    | "info"
    | "warning"
    | "yellow"
    | "blue"
    | "pink"
    | "neon"
    | "neutral"
    | undefined;
  border?: boolean;
  onClick?: React.MouseEventHandler;
  icon?: ReactNode;
};

export const Chip = ({
  size_ = "small",
  label,
  border = false,
  variant_ = "primary",
  onClick,
  className = "Button",
  icon,
  sx,
  ...props
}: ChipType) => {
  const chipProps = useMemo(() => createChipProps(Colors), [Colors]);

  const {text, borderColor, background} = chipProps.color[variant_];
  return (
    <MuiChip
      className={className}
      onClick={onClick}
      icon={icon}
      label={
        <Text variant={"inter500"} sx={{color: text, fontSize: chipProps.font[size_]}}>
          {label}
        </Text>
      }
      sx={{
        "& .MuiChip-deleteIcon": {
          fontSize: chipProps.font[size_],
          padding: "0px 0px 0px 10px",
        },
        "& .MuiChip-label": {
          padding: icon ? "0px 0px 0px 4px" : "0px",
        },
        "& .MuiChip-icon": {
          marginLeft: 0,
          color: text,
        },
        fontWeight: 500,
        borderRadius: chipProps.borderRadius[size_],
        padding: chipProps.padding[size_],
        height: chipProps.height[size_],
        whiteSpace: "nowrap",
        backgroundColor: background,
        background,
        ...(border && {border: `1px solid ${borderColor}`}),
        ...sx,
      }}
      {...props}
    />
  );
};
